//====================================================
// DynamicSelectBox
// - Treats 0 and "0" as null, never calls getId(0).
// - When FK is null/0, auto loads page 1 using current query (often empty) to show GetAll paged.
// - When FK has a value (>0), resolves label via getId(id) and also loads page 1 for the current query.
// - Emits fk-query only on real user input; no emissions on mount or prop sync.
// - Guards onChange to avoid re-emitting identical values.
// - Supports clearing the filter (reason === "clear" or text === "") and refetches from page 1.
// - Take-latest per query: older responses (incl. scroll) are ignored when the search text changes.
//====================================================
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  useCallback,
} from 'react'
import PropTypes from 'prop-types'
import { BufferService, MSelect } from 'src/M_Components'
import { LoadingContext } from 'src/M_Components'
import { isFailurePayload } from 'src/components/utils/apiResponse'

export default function DynamicSelectBox({
  label,
  tableName,
  groupBy = 'Id',
  valueId,
  onChange,
  required,
  disabled,
  pageSize = 20,
  initialQuery = '',
  onQueryChange, // parent listener to persist filter text (per tab/field)
  staticFilter = 'Inactive eq false',
  orderBy = undefined,
  buildFilter,
  clientFilterOptions,
  // optional, emits the current page of options as raw rows (API DTOs)
  onOptionsChange,
}) {
  const ctx = useContext(LoadingContext)
  const svc = useMemo(
    () => new BufferService(tableName, ctx.DEFAULTURL),
    [tableName, ctx.DEFAULTURL]
  )

  // visible state
  // NOTE: we keep filter text and selected label separated:
  // - filterText persists what the user typed (so it doesn't disappear on blur)
  // - the input shown in the box will be:
  //   * filterText while open
  //   * selected.label while closed (so selection "renders" in the box)
  const [filterText, setFilterText] = useState(initialQuery || '')
  const [options, setOptions] = useState([]) // { Id, value, label, raw }
  const [selected, setSelected] = useState(null)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  // control refs
  const lastEmittedQueryRef = useRef(null)
  const lastEmittedValueRef = useRef(null)
  const isUserTypingRef = useRef(false)
  const requestSeq = useRef(0)
  const latestApplied = useRef(0)
  const unmountedRef = useRef(false)
  const debTimer = useRef(null)

  // Query-version token: increment whenever the search text changes (or initialQuery sync happens).
  // Any in-flight response carrying an old token is ignored.
  const queryTokenRef = useRef(0)

  //  : avoid spamming onOptionsChange for identical option sets
  const lastOptionsSigRef = useRef(null)

  useEffect(() => {
    return () => {
      unmountedRef.current = true
      if (debTimer.current) clearTimeout(debTimer.current)
    }
  }, [])

  //====================================================
  // normalizeId
  // - Converts incoming id to a valid positive number or null.
  // - Treats 0, "0", empty, undefined as null to avoid getId(0).
  //====================================================
  const normalizeId = useCallback((v) => {
    if (v === 0 || v === '0' || v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [])

  //====================================================
  // mapRow
  // - Maps an API row to the local option shape.
  // - Preserves the full raw row so parents can access extra fields (e.g. TenderId).
  //====================================================
  const mapRow = useCallback(
    (r) => ({
      Id: Number(r?.Id),
      value: Number(r?.Id),
      label: String(r?.[groupBy] ?? r?.Name ?? r?.Code ?? r?.Id ?? ''),
      raw: r,
    }),
    [groupBy]
  )

  //====================================================
  // emitOptionsIfChanged
  // - Emits raw rows for the current options page.
  // - Guards so we only emit when the option set actually changed.
  //====================================================
  const emitOptionsIfChanged = useCallback(
    (items) => {
      if (typeof onOptionsChange !== 'function') return
      const list = Array.isArray(items) ? items : []

      // signature based on option ids (stable + cheap)
      const sig = list.map((x) => String(x?.value ?? x?.Id ?? '')).join('|')
      if (sig === lastOptionsSigRef.current) return
      lastOptionsSigRef.current = sig

      // Emit raw API rows only (parent screens want DTO fields like Latitude/Longitude)
      onOptionsChange(list.map((x) => x?.raw).filter(Boolean))
    },
    [onOptionsChange]
  )

  //====================================================
  // Sync incoming valueId to local selected option
  // - If null, clear selection.
  // - If same id, do nothing to avoid loops.
  //====================================================
  useEffect(() => {
    const nId = normalizeId(valueId)
    if (nId == null) {
      setSelected(null)
      return
    }
    if (selected?.value === nId) return
    setSelected((prev) => ({
      Id: nId,
      value: nId,
      label: prev?.label || `#${nId}`,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueId, normalizeId]) // do not depend on 'selected' full object

  //====================================================
  // Resolve label when we have a valid id (>0)
  // - If option already exists, reuse it.
  // - Else call getId(id) and set only the "selected" (DO NOT mutate options here).
  //   WHY: keeping the list purely API-driven avoids "phantom duplicates" when the query changes.
  //====================================================
  useEffect(() => {
    const nId = normalizeId(valueId)
    if (nId == null) return

    const hit = options.find((o) => o.value === nId)
    if (hit && selected?.label === hit.label) return

    let cancelled = false;
    (async () => {
      try {
        if (hit) {
          if (!cancelled && !unmountedRef.current) setSelected(hit)
          return
        }
        const row = await svc.getId(nId)
        if (isFailurePayload(row)) return
        const opt = mapRow(row || {})
        if (unmountedRef.current || cancelled) return
        // IMPORTANT: do not push the selected into options; keep list purely API-driven
        setSelected(opt)
      } catch {
        // keep placeholder label if request fails
      }
    })()
    return () => {
      cancelled = true
    }
  }, [valueId, options, selected?.label, svc, mapRow, normalizeId])

  //====================================================
  // Sync incoming initialQuery to input without emitting
  // - Prevents fk-query loops when parent persists and re-seeds same text.
  // - Also bumps query token so older responses are ignored.
  //====================================================
  useEffect(() => {
    const next = String(initialQuery || '')
    if (next === filterText) return
    setFilterText(next)
    queryTokenRef.current++ // invalidate any in-flight responses from the previous query
  }, [initialQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  //====================================================
  // fetchPage
  // - Fetches one page honoring contains(groupBy,'q') and Inactive eq false.
  // - Uses both request sequence and query-token to avoid out-of-order races.
  //====================================================
  const fetchPage = useCallback(
    async ({ pageToGet, queryText }) => {
      if (disabled) return { items: [] }

      const myId = ++requestSeq.current
      const myQueryToken = queryTokenRef.current

      const q = (queryText || '').trim().replace(/'/g, "''")
      const finalOrder = encodeURIComponent(orderBy || `${groupBy} asc`)
      let filterStr
      if (typeof buildFilter === 'function') {
        // delegate full filter construction to caller
        filterStr = buildFilter({ q, groupBy, staticFilter }) || ''
      } else {
        const clauses = [staticFilter].filter(Boolean)
        if (q) clauses.unshift(`contains(${groupBy},'${q}')`)
        filterStr = clauses.join(' and ')
      }
      const filter = encodeURIComponent(filterStr)

      const qs = `?Page=${pageToGet}&PageSize=${pageSize}&OrderBy=${finalOrder}&Filter=${filter}`

      try {
        const res = await svc.filter(qs)
        if (myId < requestSeq.current) return { items: [] }
        if (isFailurePayload(res)) return { items: [] }

        const raw = Array.isArray(res?.Items)
          ? res.Items
          : Array.isArray(res)
            ? res
            : []
        const mapped = raw.map(mapRow)

        if (myId >= latestApplied.current) latestApplied.current = myId

        // If the query changed while this request was in flight, drop these results.
        if (myQueryToken !== queryTokenRef.current) return { items: [] }

        return { items: mapped }
      } catch {
        return { items: [] }
      }
    },
    [disabled, groupBy, pageSize, svc, mapRow, orderBy, buildFilter, staticFilter]
  )

  //====================================================
  // Initial load
  // - Always load page 1 on mount using current query (often empty).
  // - Ensures Update with null FK still shows the first page immediately.
  //====================================================
  useEffect(() => {
    if (disabled || hasLoadedOnce) return
    setHasLoadedOnce(true)
      ; (async () => {
        const tokenAtStart = queryTokenRef.current
        const { items } = await fetchPage({
          pageToGet: 1,
          queryText: filterText,
        })
        if (unmountedRef.current || tokenAtStart !== queryTokenRef.current) return
        setOptions(items)
        emitOptionsIfChanged(items) //  
      })()
  }, [disabled, hasLoadedOnce, fetchPage, filterText, emitOptionsIfChanged])

  //====================================================
  // ensureFirstLoad
  // - Safeguard lazy load when dropdown opens for the first time.
  //====================================================
  const ensureFirstLoad = useCallback(async () => {
    if (disabled || hasLoadedOnce) return
    setHasLoadedOnce(true)
    const tokenAtStart = queryTokenRef.current
    const { items } = await fetchPage({
      pageToGet: 1,
      queryText: filterText,
    })
    if (unmountedRef.current || tokenAtStart !== queryTokenRef.current) return
    setOptions(items)
    emitOptionsIfChanged(items) //  
  }, [disabled, hasLoadedOnce, fetchPage, filterText, emitOptionsIfChanged])

  //====================================================
  // safeEmitQuery
  // - Emits onQueryChange only for real user input and only if text actually changed.
  //====================================================
  const safeEmitQuery = useCallback(
    (q) => {
      if (typeof onQueryChange !== 'function') return
      if (!isUserTypingRef.current) return
      if (lastEmittedQueryRef.current === q) return
      lastEmittedQueryRef.current = q
      onQueryChange(q)
    },
    [onQueryChange]
  )

  //====================================================
  // onUserInput
  // - Debounces refetch for user typed text, including empty string when cleared.
  // - Resets paging to page 1.
  // - Bumps query token so old responses cannot apply.
  //====================================================
  const onUserInput = useCallback(
    (text) => {
      isUserTypingRef.current = true
      setFilterText(text)
      safeEmitQuery(text)
      queryTokenRef.current++ // invalidate older requests

      if (debTimer.current) clearTimeout(debTimer.current)
      debTimer.current = setTimeout(async () => {
        const tokenAtStart = queryTokenRef.current
        const { items } = await fetchPage({
          pageToGet: 1,
          queryText: text,
        })
        if (unmountedRef.current || tokenAtStart !== queryTokenRef.current) return
        setOptions(items)
        emitOptionsIfChanged(items) //  
      }, 150)
    },
    [fetchPage, safeEmitQuery, emitOptionsIfChanged]
  )

  //====================================================
  // onOpen
  // - Ensures first page exists before user scrolls.
  //====================================================
  const onOpen = useCallback(() => {
    setIsOpen(true)
    ensureFirstLoad()
  }, [ensureFirstLoad])

  //====================================================
  // onClose
  // - When clicking outside, MUI may "reset" the input to the selected label (or empty) unless controlled.
  // - We keep the typed filter text persisted (filterText),
  //   but when closed we render selected.label in the box.
  //====================================================
  const onClose = useCallback(() => {
    setIsOpen(false)
    isUserTypingRef.current = false
  }, [])

  //====================================================
  // inputValueShown
  // - While open: show filterText (user's typed filter)
  // - While closed: show selected label (so selection persists/renders), fallback to filterText
  //====================================================
  const inputValueShown = useMemo(() => {
    if (isOpen) return filterText
    return selected?.label ?? filterText
  }, [isOpen, filterText, selected?.label])

  return (
    <div>
      <MSelect
        label={label}
        autocomplete
        required={!!required}
        disabled={!!disabled}
        options={options}
        value={selected}

        //====================================================
        // Keep filter text visible even after blur/click-outside:
        // - Control Autocomplete inputValue
        // - Disable clearOnBlur so MUI doesn't revert the text
        // - Show selected label when closed so chosen option persists/renders
        //====================================================
        inputValue={inputValueShown}
        clearOnBlur={false}
        onClose={onClose}

        //====================================================
        // Selection change
        // - If user clears the selection (sel === null):
        //   * clear the selected option
        //   * clear the input text
        //   * emit empty query to parent (so tabs remember no filter)
        //   * refetch page 1 with empty query (unfiltered)
        //   * notify parent onChange(null) so FK becomes null
        // - Otherwise, emit only when the value actually changes.
        //====================================================
        onChangeValue={(sel) => {
          const next = sel || null
          const nextVal = next ? next.value : null

          // Clearing selection path
          if (!next) {
            lastEmittedValueRef.current = null
            setSelected(null)
            setFilterText('')
            if (typeof onQueryChange === 'function') onQueryChange('')

              // Refetch page 1 with empty query to show unfiltered list
              ; (async () => {
                const tokenAtStart = ++queryTokenRef.current
                const { items } = await fetchPage({
                  pageToGet: 1,
                  queryText: '',
                })
                if (!unmountedRef.current && tokenAtStart === queryTokenRef.current) {
                  setOptions(items)
                  emitOptionsIfChanged(items) //  
                }
              })()

            onChange?.(null)
            return
          }

          // Normal selection path (avoid re-emitting identical value)
          if (lastEmittedValueRef.current === nextVal) {
            setSelected(next)
            return
          }
          lastEmittedValueRef.current = nextVal
          setSelected(next)
          onChange?.(next)
        }}

        //====================================================
        // Input change
        // - reason "input": user typed -> debounce refetch with given text
        // - reason "clear": user clicked clear -> treat as empty string and refetch
        // - reason "reset": programmatic reset -> ignore to avoid loops
        //====================================================
        onInputChange={(_, v, reason) => {
          if (reason === 'input') onUserInput(v || '')
          if (reason === 'clear') onUserInput('')
        }}
        ListboxProps={{
          style: { maxHeight: 196, overflow: 'auto' },
        }}
        onOpen={onOpen}
        // IMPORTANT: disable client filtering; we supply already-filtered options
        filterOptions={(xs) =>
          typeof clientFilterOptions === 'function'
            ? clientFilterOptions(xs)
            : xs
        }
      />
    </div>
  )
}

DynamicSelectBox.propTypes = {
  label: PropTypes.string,
  tableName: PropTypes.string.isRequired,
  groupBy: PropTypes.string,
  valueId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  pageSize: PropTypes.number,
  initialQuery: PropTypes.string,
  onQueryChange: PropTypes.func,
  staticFilter: PropTypes.string,
  orderBy: PropTypes.string,
  buildFilter: PropTypes.func,
  clientFilterOptions: PropTypes.func,
  onOptionsChange: PropTypes.func,
}
